import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { LayoutDashboard, Image, Package, FolderOpen, FileText, Phone, MessageSquare, Users, Settings, LogOut, Download, Trash2, Eye, ToggleLeft, ToggleRight, Plus, Edit2, Save, X, BarChart3, Smartphone, Monitor, Globe, ExternalLink, TrendingUp, Code2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const CodeEditorModule = lazy(() => import('@/components/CodeEditorModule'));

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadsTypeFilter, setLeadsTypeFilter] = useState('');
  const [hero, setHero] = useState({});
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [catalogPdf, setCatalogPdf] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  useEffect(() => {
    const token = localStorage.getItem('blackline_token');
    const storedUser = localStorage.getItem('blackline_user');
    if (!token) { navigate('/admin'); return; }
    if (storedUser) setUser(JSON.parse(storedUser));
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [statsRes, settingsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/site-settings')
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); }
    }
  };

  const loadLeads = useCallback(async () => {
    try {
      const res = await api.get('/admin/leads', { params: { search: leadsSearch, type_filter: leadsTypeFilter, page: leadsPage } });
      setLeads(res.data.leads);
      setLeadsTotal(res.data.total);
    } catch (err) { console.error(err); }
  }, [leadsSearch, leadsTypeFilter, leadsPage]);

  useEffect(() => { if (activeTab === 'leads') loadLeads(); }, [activeTab, loadLeads]);

  const loadHero = async () => {
    try { const res = await api.get('/admin/hero'); setHero(res.data); } catch (err) { console.error(err); }
  };

  const loadProducts = async () => {
    try { const res = await api.get('/admin/products'); setProducts(res.data); } catch (err) { console.error(err); }
  };

  const loadProjects = async () => {
    try { const res = await api.get('/admin/projects'); setProjects(res.data); } catch (err) { console.error(err); }
  };

  const loadUsers = async () => {
    try { const res = await api.get('/admin/users'); setUsers(res.data); } catch (err) { console.error(err); }
  };

  const loadCatalog = async () => {
    try { const res = await api.get('/catalog'); setCatalogPdf(res.data?.pdf_url || ''); } catch (err) { console.error(err); }
  };

  const loadAnalytics = useCallback(async () => {
    try { const res = await api.get(`/admin/analytics?days=${analyticsDays}`); setAnalytics(res.data); } catch (err) { console.error(err); }
  }, [analyticsDays]);

  useEffect(() => {
    if (activeTab === 'hero') loadHero();
    if (activeTab === 'products') loadProducts();
    if (activeTab === 'gallery') loadProjects();
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'catalog') loadCatalog();
    if (activeTab === 'dashboard') loadAnalytics();
  }, [activeTab]);

  const logout = () => {
    localStorage.removeItem('blackline_token');
    localStorage.removeItem('blackline_user');
    navigate('/admin');
  };

  const showMessage = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const saveHero = async () => {
    setSaving(true);
    try {
      await api.put('/admin/hero', hero);
      showMessage('Hero updated!');
    } catch (err) { showMessage('Error saving'); }
    setSaving(false);
  };

  const handleUpload = async (file, callback) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      showMessage('Uploading...');
      const res = await api.post('/admin/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.url) {
        callback(res.data.url);
      } else {
        showMessage('Upload failed: no URL returned');
      }
    } catch (err) {
      console.error('Upload error:', err);
      showMessage('Upload failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.delete(`/admin/products/${id}`); loadProducts(); showMessage('Product deleted'); } catch (err) { showMessage('Error'); }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try { await api.delete(`/admin/projects/${id}`); loadProjects(); showMessage('Project deleted'); } catch (err) { showMessage('Error'); }
  };

  const toggleMaintenance = async () => {
    const current = settings?.maintenance_mode || false;
    try {
      await api.put('/admin/settings', { maintenance_mode: !current });
      setSettings({ ...settings, maintenance_mode: !current });
      showMessage(!current ? 'Maintenance mode ON' : 'Maintenance mode OFF');
    } catch (err) { showMessage('Error: ' + (err.response?.data?.detail || 'Failed')); }
  };

  const toggleUser = async (userId) => {
    try { await api.put(`/admin/users/${userId}/toggle`); loadUsers(); } catch (err) { showMessage('Error'); }
  };

  const exportCSV = () => {
    const token = localStorage.getItem('blackline_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backendUrl}/api/admin/leads/export?token=${token}`, '_blank');
  };

  const isSuperAdmin = user?.role === 'super_admin';

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> },
    { id: 'hero', label: 'Hero', icon: <Image size={18} /> },
    { id: 'products', label: 'Products', icon: <Package size={18} /> },
    { id: 'gallery', label: 'Gallery', icon: <FolderOpen size={18} /> },
    { id: 'catalog', label: 'Catalog', icon: <FileText size={18} /> },
    { id: 'contact', label: 'Contact Info', icon: <Phone size={18} /> },
    { id: 'leads', label: 'Leads', icon: <MessageSquare size={18} /> },
    ...(isSuperAdmin ? [
      { id: 'users', label: 'Users', icon: <Users size={18} /> },
      { id: 'code', label: 'Editor de Código', icon: <Code2 size={18} /> },
      { id: 'settings', label: 'Settings', icon: <Settings size={18} /> }
    ] : []),
  ];

  return (
    <div className="admin-layout" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <img src="/blackline-logo-cropped.png" alt="Blackline Premium Solutions" className="admin-logo-img" />
          <small>Admin Panel</small>
        </div>
        <nav className="admin-sidebar-nav">
          {tabs.map(tab => (
            <button key={tab.id} className={activeTab === tab.id ? 'active' : ''} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '0.8rem' }}>
            {user?.name} ({user?.role?.replace('_', ' ')})
          </p>
          <button onClick={logout} data-testid="logout-btn"><LogOut size={14} /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        {message && (
          <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999, background: 'var(--gold)', color: 'var(--black)', padding: '0.8rem 1.5rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            {message}
          </div>
        )}

        {/* DASHBOARD */}
        {activeTab === 'dashboard' && (
          <div>
            <div className="admin-header">
              <h1>Dashboard</h1>
              <a href="https://instruction-guide-15.preview.emergentagent.com" target="_blank" rel="noopener noreferrer" className="admin-btn admin-btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <ExternalLink size={14} /> View Site
              </a>
            </div>

            {/* Site Overview */}
            <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>Site Overview</h3>
                <span style={{ fontSize: '0.72rem', color: 'var(--gray-light)' }}>Created: March 2026</span>
              </div>
              <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                <select value={analyticsDays} onChange={(e) => setAnalyticsDays(Number(e.target.value))} style={{ padding: '0.5rem 0.8rem', border: '1px solid #e0e0e0', borderRadius: '6px', fontFamily: 'var(--font-sans)', fontSize: '0.82rem' }}>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last year</option>
                </select>
                <button className="admin-btn admin-btn-outline" onClick={loadAnalytics} style={{ fontSize: '0.78rem' }}>Refresh</button>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <div className="stat-label"><TrendingUp size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} /> Visits ({analyticsDays}d)</div>
                <div className="stat-value">{analytics?.total_visits || 0}</div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-label"><Smartphone size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} /> Mobile</div>
                <div className="stat-value" style={{ color: 'var(--gold)' }}>{analytics?.mobile_pct || 0}%</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--gray-light)' }}>{analytics?.mobile_visits || 0} visits</div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-label"><Monitor size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} /> Desktop</div>
                <div className="stat-value">{analytics?.desktop_pct || 0}%</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--gray-light)' }}>{analytics?.desktop_visits || 0} visits</div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-label"><MessageSquare size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} /> Leads</div>
                <div className="stat-value" style={{ color: 'var(--gold)' }}>{stats.unread_leads || 0}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--gray-light)' }}>{stats.total_leads || 0} total</div>
              </div>
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              {/* Daily Visits Chart */}
              <div className="admin-card">
                <h3><BarChart3 size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Daily Visits</h3>
                {analytics?.daily && analytics.daily.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.daily}>
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.substring(5)} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: '0.82rem', fontFamily: 'Outfit' }} />
                      <Bar dataKey="visits" fill="#b8976a" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-light)', fontSize: '0.88rem' }}>
                    No visit data yet. Stats will appear as your site receives traffic.
                  </div>
                )}
              </div>

              {/* Device Breakdown Pie */}
              <div className="admin-card">
                <h3>Device Breakdown</h3>
                {analytics && analytics.total_visits > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={[
                          { name: 'Mobile', value: analytics.mobile_visits || 0 },
                          { name: 'Desktop', value: analytics.desktop_visits || 0 }
                        ]} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value">
                          <Cell fill="#b8976a" />
                          <Cell fill="#292524" />
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: '0.82rem', fontFamily: 'Outfit' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.75rem' }}>
                      <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#b8976a', marginRight: 4 }} />Mobile {analytics.mobile_pct}%</span>
                      <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#292524', marginRight: 4 }} />Desktop {analytics.desktop_pct}%</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-light)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
                    <Smartphone size={20} style={{ marginRight: '0.5rem', opacity: 0.4 }} /> Device data will appear with traffic
                  </div>
                )}
              </div>
            </div>

            {/* Top Pages */}
            {analytics?.top_pages && analytics.top_pages.length > 0 && (
              <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
                <h3>Top Pages</h3>
                <table className="admin-table">
                  <thead><tr><th>Page</th><th style={{ textAlign: 'right' }}>Visits</th></tr></thead>
                  <tbody>
                    {analytics.top_pages.map((p, i) => (
                      <tr key={i}><td>{p.page}</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{p.visits}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Quick Stats Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="admin-stat-card">
                <div className="stat-label">Products</div>
                <div className="stat-value">{stats.total_products || 0}</div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-label">Gallery Projects</div>
                <div className="stat-value">{stats.total_projects || 0}</div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-label">All-Time Visits</div>
                <div className="stat-value">{analytics?.total_all_time || 0}</div>
              </div>
            </div>

            {/* Google Analytics Integration */}
            <div className="admin-card">
              <h3><Globe size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Google Analytics Integration</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--gray)', marginBottom: '1rem' }}>
                Connect your Google Analytics 4 property to track detailed visitor behavior, traffic sources, conversions, and more.
              </p>
              <div className="admin-field">
                <label>GA4 Measurement ID</label>
                <input placeholder="G-XXXXXXXXXX" defaultValue={settings?.ga4_id || ''} onChange={(e) => setSettings({...settings, ga4_id: e.target.value})} />
              </div>
              <div className="admin-field">
                <label>GTM Container ID (optional)</label>
                <input placeholder="GTM-XXXXXXX" defaultValue={settings?.gtm_id || ''} onChange={(e) => setSettings({...settings, gtm_id: e.target.value})} />
              </div>
              <button className="admin-btn admin-btn-primary" onClick={async () => {
                try {
                  await api.put('/admin/contact', { ...settings?.contact, ga4_id: settings?.ga4_id, gtm_id: settings?.gtm_id });
                  showMessage('Analytics settings saved!');
                } catch (err) { showMessage('Error saving'); }
              }}>Save Analytics Settings</button>
              <p style={{ fontSize: '0.72rem', color: 'var(--gray-light)', marginTop: '0.8rem' }}>
                Don't have Google Analytics? <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>Create a free account</a> and get your Measurement ID.
              </p>
            </div>
          </div>
        )}

        {/* HERO EDITOR */}
        {activeTab === 'hero' && (
          <div>
            <div className="admin-header"><h1>Hero Section</h1></div>
            <div className="admin-card">
              <h3>Background Image</h3>
              <div className="admin-field">
                <label>Image URL</label>
                <input value={hero?.image_url || ''} onChange={(e) => setHero({...hero, image_url: e.target.value})} />
              </div>
              <div className="admin-field">
                <label>Or Upload Image</label>
                <input type="file" accept="image/*" onChange={(e) => {
                  if (e.target.files[0]) handleUpload(e.target.files[0], (url) => setHero({...hero, image_url: url}));
                }} />
              </div>
              {hero?.image_url && <img src={hero.image_url} alt="Hero preview" style={{ maxWidth: '300px', marginTop: '0.5rem', borderRadius: '4px' }} />}
            </div>
            <div className="admin-card">
              <h3>English Content</h3>
              <div className="admin-field">
                <label>Eyebrow</label>
                <input value={hero?.en?.eyebrow || ''} onChange={(e) => setHero({...hero, en: {...(hero?.en || {}), eyebrow: e.target.value}})} />
              </div>
              <div className="admin-field">
                <label>Headline</label>
                <input value={hero?.en?.headline || ''} onChange={(e) => setHero({...hero, en: {...(hero?.en || {}), headline: e.target.value}})} />
              </div>
              <div className="admin-field">
                <label>Subhead</label>
                <textarea value={hero?.en?.subhead || ''} onChange={(e) => setHero({...hero, en: {...(hero?.en || {}), subhead: e.target.value}})} />
              </div>
            </div>
            <div className="admin-card">
              <h3>Spanish Content</h3>
              <div className="admin-field">
                <label>Eyebrow (ES)</label>
                <input value={hero?.es?.eyebrow || ''} onChange={(e) => setHero({...hero, es: {...(hero?.es || {}), eyebrow: e.target.value}})} />
              </div>
              <div className="admin-field">
                <label>Headline (ES)</label>
                <input value={hero?.es?.headline || ''} onChange={(e) => setHero({...hero, es: {...(hero?.es || {}), headline: e.target.value}})} />
              </div>
              <div className="admin-field">
                <label>Subhead (ES)</label>
                <textarea value={hero?.es?.subhead || ''} onChange={(e) => setHero({...hero, es: {...(hero?.es || {}), subhead: e.target.value}})} />
              </div>
            </div>
            <button className="admin-btn admin-btn-primary" onClick={saveHero} disabled={saving} data-testid="save-hero">
              {saving ? 'Saving...' : 'Save Hero'}
            </button>
          </div>
        )}

        {/* PRODUCTS */}
        {activeTab === 'products' && (
          <div>
            <div className="admin-header">
              <h1>Products</h1>
            </div>
            {/* Edit Modal */}
            {editingProduct && (
              <div className="admin-card" style={{ border: '2px solid var(--gold)', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>Edit: {editingProduct.en?.name}</h3>
                  <button className="admin-btn admin-btn-outline" onClick={() => setEditingProduct(null)}><X size={14} /> Cancel</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                  <div>
                    <h4 style={{ fontSize: '0.85rem', color: 'var(--gold)', marginBottom: '1rem' }}>English</h4>
                    <div className="admin-field">
                      <label>Name</label>
                      <input value={editingProduct.en?.name || ''} onChange={(e) => setEditingProduct({...editingProduct, en: {...editingProduct.en, name: e.target.value}})} />
                    </div>
                    <div className="admin-field">
                      <label>One-liner</label>
                      <input value={editingProduct.en?.one_liner || ''} onChange={(e) => setEditingProduct({...editingProduct, en: {...editingProduct.en, one_liner: e.target.value}})} />
                    </div>
                    <div className="admin-field">
                      <label>Description</label>
                      <textarea value={editingProduct.en?.description || ''} onChange={(e) => setEditingProduct({...editingProduct, en: {...editingProduct.en, description: e.target.value}})} />
                    </div>
                  </div>
                  <div>
                    <h4 style={{ fontSize: '0.85rem', color: 'var(--gold)', marginBottom: '1rem' }}>Espa&ntilde;ol</h4>
                    <div className="admin-field">
                      <label>Nombre</label>
                      <input value={editingProduct.es?.name || ''} onChange={(e) => setEditingProduct({...editingProduct, es: {...editingProduct.es, name: e.target.value}})} />
                    </div>
                    <div className="admin-field">
                      <label>Frase corta</label>
                      <input value={editingProduct.es?.one_liner || ''} onChange={(e) => setEditingProduct({...editingProduct, es: {...editingProduct.es, one_liner: e.target.value}})} />
                    </div>
                    <div className="admin-field">
                      <label>Descripci&oacute;n</label>
                      <textarea value={editingProduct.es?.description || ''} onChange={(e) => setEditingProduct({...editingProduct, es: {...editingProduct.es, description: e.target.value}})} />
                    </div>
                  </div>
                </div>
                <div className="admin-field" style={{ marginTop: '1rem' }}>
                  <label>Image URL</label>
                  <input value={editingProduct.image_url || ''} onChange={(e) => setEditingProduct({...editingProduct, image_url: e.target.value})} />
                </div>
                <div className="admin-field">
                  <label>Or Upload Image</label>
                  <input type="file" accept="image/*" onChange={(e) => {
                    if (e.target.files[0]) handleUpload(e.target.files[0], (url) => setEditingProduct({...editingProduct, image_url: url}));
                  }} />
                </div>
                <button className="admin-btn admin-btn-primary" style={{ marginTop: '1rem' }} onClick={async () => {
                  try {
                    await api.put(`/admin/products/${editingProduct.id}`, editingProduct);
                    showMessage('Product updated!');
                    setEditingProduct(null);
                    loadProducts();
                  } catch (err) { showMessage('Error saving'); }
                }}><Save size={14} /> Save Product</button>
              </div>
            )}
            {products.map(p => (
              <div className="admin-card" key={p.id} style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                {p.image_url && <img src={p.image_url} alt={p.en?.name} style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '4px' }} />}
                <div style={{ flex: 1 }}>
                  <h3 style={{ marginBottom: '0.3rem' }}>{p.en?.name || p.slug}</h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>{p.en?.one_liner}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.2rem' }}>ES: {p.es?.name} &mdash; {p.es?.one_liner}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="admin-btn admin-btn-outline" onClick={() => setEditingProduct({...p})} style={{ padding: '0.4rem 0.8rem' }}><Edit2 size={14} /></button>
                  {isSuperAdmin && (
                    <button className="admin-btn admin-btn-danger" onClick={() => deleteProduct(p.id)} style={{ padding: '0.4rem 0.8rem' }}><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* GALLERY */}
        {activeTab === 'gallery' && (
          <div>
            <div className="admin-header">
              <h1>Gallery / Projects</h1>
              <button className="admin-btn admin-btn-primary" onClick={async () => {
                const title = prompt('Project title (EN):');
                if (!title) return;
                const location = prompt('Location:') || '';
                await api.post('/admin/projects', {
                  en: { title, location },
                  es: { title, location },
                  image_url: '',
                  order: projects.length + 1
                });
                loadProjects();
              }}><Plus size={14} /> Add Project</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
              {projects.map(p => (
                <div className="admin-card" key={p.id} style={{ padding: '1rem' }}>
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.en?.title} style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.8rem' }} />
                  ) : (
                    <div style={{ width: '100%', height: '180px', background: '#f0f0f0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.8rem' }}>No image</div>
                  )}
                  <h4 style={{ fontSize: '0.95rem', marginBottom: '0.3rem' }}>{p.en?.title}</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>{p.en?.location}</p>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.8rem' }}>
                    <input type="file" accept="image/*" style={{ fontSize: '0.75rem' }} onChange={(e) => {
                      if (e.target.files[0]) handleUpload(e.target.files[0], async (url) => {
                        await api.put(`/admin/projects/${p.id}`, { image_url: url });
                        loadProjects();
                        showMessage('Image updated');
                      });
                    }} />
                    <button className="admin-btn admin-btn-danger" onClick={() => deleteProject(p.id)} style={{ padding: '0.4rem 0.8rem' }}><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CATALOG */}
        {activeTab === 'catalog' && (
          <div>
            <div className="admin-header"><h1>Catalog</h1></div>
            <div className="admin-card">
              <h3>Product Catalog PDF</h3>
              <div className="admin-field">
                <label>Current PDF URL</label>
                <input value={catalogPdf} onChange={(e) => setCatalogPdf(e.target.value)} placeholder="No catalog uploaded yet" />
              </div>
              <div style={{ display: 'flex', gap: '0.8rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                {catalogPdf && (
                  <a href={catalogPdf.startsWith('http') ? catalogPdf : `${process.env.REACT_APP_BACKEND_URL}${catalogPdf}`} target="_blank" rel="noopener noreferrer" className="admin-btn admin-btn-outline" style={{ fontSize: '0.78rem' }}>
                    View Current PDF
                  </a>
                )}
                <button className="admin-btn admin-btn-primary" style={{ fontSize: '0.78rem' }} onClick={async () => {
                  try {
                    await api.put('/admin/catalog', { pdf_url: catalogPdf });
                    showMessage('Catalog URL saved!');
                  } catch (err) { showMessage('Error saving: ' + (err.response?.data?.detail || err.message)); }
                }}>
                  Save URL
                </button>
              </div>
              <div className="admin-field" style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1.5rem' }}>
                <label>Upload New PDF</label>
                <input type="file" accept=".pdf" onChange={(e) => {
                  if (e.target.files[0]) handleUpload(e.target.files[0], async (url) => {
                    try {
                      await api.put('/admin/catalog', { pdf_url: url });
                      setCatalogPdf(url);
                      showMessage('Catalog PDF uploaded and saved!');
                    } catch (err) { showMessage('Uploaded but failed to save URL'); }
                  });
                }} />
                <p style={{ fontSize: '0.72rem', color: 'var(--gray-light)', marginTop: '0.5rem' }}>Upload a PDF file. It will be saved and set as the download link on the website.</p>
              </div>
            </div>
          </div>
        )}

        {/* CONTACT INFO */}
        {activeTab === 'contact' && (
          <div>
            <div className="admin-header"><h1>Contact Information</h1></div>
            <div className="admin-card">
              <h3>Contact Details</h3>
              <div className="admin-field">
                <label>WhatsApp Number</label>
                <input value={settings?.contact?.whatsapp || ''} onChange={(e) => setSettings({...settings, contact: {...settings?.contact, whatsapp: e.target.value}})} />
              </div>
              <div className="admin-field">
                <label>Email</label>
                <input value={settings?.contact?.email || ''} onChange={(e) => setSettings({...settings, contact: {...settings?.contact, email: e.target.value}})} />
              </div>
              <div className="admin-field">
                <label>Houston Phone</label>
                <input value={settings?.contact?.houston_phone || ''} onChange={(e) => setSettings({...settings, contact: {...settings?.contact, houston_phone: e.target.value}})} />
              </div>
              <div className="admin-field">
                <label>Dallas Phone</label>
                <input value={settings?.contact?.dallas_phone || ''} onChange={(e) => setSettings({...settings, contact: {...settings?.contact, dallas_phone: e.target.value}})} />
              </div>
              <div className="admin-field">
                <label>Business Hours</label>
                <input value={settings?.contact?.hours || ''} onChange={(e) => setSettings({...settings, contact: {...settings?.contact, hours: e.target.value}})} />
              </div>
              <button className="admin-btn admin-btn-primary" onClick={async () => {
                try {
                  await api.put('/admin/contact', settings?.contact || {});
                  showMessage('Contact info updated');
                } catch (err) { showMessage('Error saving'); }
              }} data-testid="save-contact">Save Contact Info</button>
            </div>
          </div>
        )}

        {/* LEADS */}
        {activeTab === 'leads' && (
          <div>
            <div className="admin-header">
              <h1>Leads / Messages ({leadsTotal})</h1>
              <button className="admin-btn admin-btn-outline" onClick={exportCSV} data-testid="export-csv"><Download size={14} /> Export CSV</button>
            </div>
            <div className="admin-card" style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <input placeholder="Search by name or email..." value={leadsSearch} onChange={(e) => { setLeadsSearch(e.target.value); setLeadsPage(1); }} style={{ flex: 1, padding: '0.6rem 0.8rem', border: '1px solid #e0e0e0', borderRadius: '6px', fontFamily: 'var(--font-sans)', fontSize: '0.88rem' }} data-testid="leads-search" />
                <select value={leadsTypeFilter} onChange={(e) => { setLeadsTypeFilter(e.target.value); setLeadsPage(1); }} style={{ padding: '0.6rem 0.8rem', border: '1px solid #e0e0e0', borderRadius: '6px', fontFamily: 'var(--font-sans)', fontSize: '0.88rem' }} data-testid="leads-filter">
                  <option value="">All Types</option>
                  <option value="Homeowner">Homeowner</option>
                  <option value="Builder / Contractor">Builder / Contractor</option>
                  <option value="Architect / Designer">Architect / Designer</option>
                  <option value="Developer">Developer</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Page</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id} style={{ cursor: 'pointer' }} onClick={async () => {
                      if (!lead.read) {
                        await api.put(`/admin/leads/${lead.id}/read`);
                        loadLeads();
                      }
                      alert(`Name: ${lead.first_name} ${lead.last_name}\nEmail: ${lead.email}\nPhone: ${lead.phone}\nType: ${lead.type}\nMessage: ${lead.message}`);
                    }}>
                      <td>{new Date(lead.created_at).toLocaleDateString()}</td>
                      <td style={{ fontWeight: lead.read ? 400 : 600 }}>{lead.first_name} {lead.last_name}</td>
                      <td>{lead.email}</td>
                      <td>{lead.type}</td>
                      <td>{lead.page}</td>
                      <td>{lead.read ? <Eye size={14} style={{ color: 'var(--gray-400)' }} /> : <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--gold)', display: 'inline-block' }} />}</td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-400)' }}>No leads found</td></tr>
                  )}
                </tbody>
              </table>
              {leadsTotal > 50 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                  <button className="admin-btn admin-btn-outline" disabled={leadsPage <= 1} onClick={() => setLeadsPage(p => p - 1)}>Prev</button>
                  <span style={{ padding: '0.7rem', fontSize: '0.85rem' }}>Page {leadsPage}</span>
                  <button className="admin-btn admin-btn-outline" onClick={() => setLeadsPage(p => p + 1)}>Next</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* USERS (Super Admin) */}
        {activeTab === 'users' && isSuperAdmin && (
          <div>
            <div className="admin-header">
              <h1>User Management</h1>
              <button className="admin-btn admin-btn-primary" onClick={async () => {
                const name = prompt('User name:');
                if (!name) return;
                const email = prompt('Email:');
                if (!email) return;
                const password = prompt('Password:');
                if (!password) return;
                const role = prompt('Role (super_admin or client_admin):', 'client_admin');
                try {
                  await api.post('/admin/users', { name, email, password, role });
                  loadUsers();
                  showMessage('User created');
                } catch (err) { showMessage(err.response?.data?.detail || 'Error'); }
              }}><Plus size={14} /> Add User</button>
            </div>
            <div className="admin-card">
              <table className="admin-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td style={{ textTransform: 'capitalize' }}>{u.role?.replace('_', ' ')}</td>
                      <td>{u.active ? <span style={{ color: 'green' }}>Active</span> : <span style={{ color: '#ef4444' }}>Inactive</span>}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="admin-btn admin-btn-outline" onClick={() => toggleUser(u.id)} style={{ padding: '0.3rem 0.6rem' }}>
                            {u.active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                          </button>
                          <button className="admin-btn admin-btn-danger" onClick={async () => {
                            if (!window.confirm(`Delete user ${u.name}?`)) return;
                            await api.delete(`/admin/users/${u.id}`);
                            loadUsers();
                          }} style={{ padding: '0.3rem 0.6rem' }}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SETTINGS (Super Admin) */}
        {activeTab === 'settings' && isSuperAdmin && (
          <div>
            <div className="admin-header"><h1>Settings</h1></div>
            <div className="admin-card">
              <h3>Maintenance Mode</h3>
              <p style={{ fontSize: '0.88rem', color: 'var(--gray-600)', marginBottom: '1rem' }}>
                When enabled, the public site shows "Under Maintenance" page. Admin panel remains accessible.
              </p>
              <button
                className={`admin-btn ${settings?.maintenance_mode ? 'admin-btn-danger' : 'admin-btn-primary'}`}
                onClick={toggleMaintenance}
                data-testid="toggle-maintenance"
              >
                {settings?.maintenance_mode ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
              </button>
              <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: '0.5rem' }}>
                Status: {settings?.maintenance_mode ? 'ACTIVE - Site is under maintenance' : 'Inactive - Site is live'}
              </p>
            </div>
            <div className="admin-card">
              <h3>Change Password</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const form = new FormData(e.target);
                try {
                  await api.put('/admin/password', {
                    current_password: form.get('current'),
                    new_password: form.get('new')
                  });
                  showMessage('Password changed!');
                  e.target.reset();
                } catch (err) { showMessage(err.response?.data?.detail || 'Error changing password'); }
              }}>
                <div className="admin-field">
                  <label>Current Password</label>
                  <input type="password" name="current" required />
                </div>
                <div className="admin-field">
                  <label>New Password</label>
                  <input type="password" name="new" required minLength={6} />
                </div>
                <button type="submit" className="admin-btn admin-btn-primary">Change Password</button>
              </form>
            </div>
          </div>
        )}

        {/* CODE EDITOR */}
        {activeTab === 'code' && isSuperAdmin && (
          <Suspense fallback={<div style={{ padding: '2rem', color: '#a8a29e' }}>Loading editor...</div>}>
            <CodeEditorModule />
          </Suspense>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
